import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {useState} from 'react';
import {useMemo} from 'react';
import {EventWizardDetails} from '@Components/event-wizard/event-wizard-details';
import {EventNav} from '@Components/event-wizard/event-nav';
import {EventFooter} from '@Components/event-wizard/event-footer';
import {Preview} from '@Components/event-wizard/preview';
import {EventWizardReview} from '@Components/event-wizard/event-wizard-review';
import {EventWizardTicketing} from '@Components/event-wizard/event-wizard-ticketing';
import type {EventWizardContainerQuery, EventWizardProps, EventWizardReduxState} from '@Components/event-wizard/event-wizard.types';
import {EventWizardProgressStep, EventWizardSourceType} from '@Components/event-wizard/event-wizard.types';
import {useDispatch} from 'react-redux';
import {
  getDefaultEventTeamShareDetails,
  getDefaultOrganiserDetails,
  getDefaultRegistrationDetails,
  onEventWizardAllTypesChange,
  onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate,
  onEventWizardGoogleMapAPIKeyChange,
  onEventWizardOrganiserUpdate,
  onEventWizardPageSourceTypeUpdate,
  onEventWizardRegistrationChange,
  onEventWizardShareInTeamToggle,
  onEventWizardShouldUpsellRecurring,
  onEventWizardTeamNameUpdate,
  onEventWizardTicketingGatingPropsUpdate,
  onEventWizardUpdateShouldShowRecurringEventDetails,
  onEventWizardUserPastOfflineVenueChange,
} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardCurrentProgressStep, useEventWizardIsEventBeingCreated, useEventWizardWizardContainerQuery} from '@Components/event-wizard/event-wizard.hooks';
import {setReduxForDesignPrefill, setReduxForStartDatePrefill, setReduxForUpdatingEvent} from '@Components/event-wizard/event-wizard.helper';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import styles from './event-wizard.module.scss';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

export function EventWizard({
  googleMapAPIKey,
  organisers,
  types,
  teamName,
  teamSize,
  customRegisterBtnTexts,
  userPastVenues,
  eventWizardSourceType,
  eventVO,
  eventVOMinifiedWizardPrefill,
  doesPAYGUserHaveAITemplateLimit,
  shouldUpsellRecurring,
  shouldShowRecurringEventDetails,
  isTicketingCountrySupported,
  isPaidTicketingFeatureAvailable,
  notifyMeEmail,
}: EventWizardProps): ReactElement<any> {
  const [isWizardReady, setIsWizardReady] = useState(false);
  const dispatch = useDispatch();
  const currentStep = useEventWizardCurrentProgressStep();
  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useEventWizardWizardContainerQuery();
  const reduxDataRef = useRef<EventWizardReduxState | null>(null);
  const isEventBeingCreated = useEventWizardIsEventBeingCreated();

  const eventWizardContainerQueriesContextValues: EventWizardContainerQuery = useMemo(() => {
    return {
      isXXsmallScreen: containerQueryParamProps.isXXsmallScreen,
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  useEffect(() => {
    setDefaultData();
    dispatch(onEventWizardGoogleMapAPIKeyChange(googleMapAPIKey));
    try {
      setDataByPageSourceType();
    } catch (error) {
      console.log(error);
      setDefaultData();
    }
    setIsWizardReady(true);
  }, []);

  useEffect(() => {
    if (isWizardReady) {
      reduxDataRef.current = window.PMW.redux.store.getState().eventWizard;
    }
  }, [isWizardReady]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      if (!shouldUnloadPage()) {
        e.preventDefault();
        return '';
      }
      return undefined;
    };

    const shouldUnloadPage = (): boolean => {
      if (!reduxDataRef.current || isEventBeingCreated) {
        return true;
      }

      const currentState = window.PMW.redux.store.getState().eventWizard;
      const currentStateWithoutTypes = omit(currentState, 'types');
      const refStateWithoutTypes = omit(reduxDataRef.current, 'types');

      return isEqual(
        {...currentStateWithoutTypes, selectedType: currentState.types.selectedType},
        {...refStateWithoutTypes, selectedType: reduxDataRef.current.types.selectedType}
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEventBeingCreated]);

  const setDefaultData = (): void => {
    dispatch(onEventWizardUserPastOfflineVenueChange(userPastVenues));
    dispatch(onEventWizardAllTypesChange(types));
    dispatch(
      onEventWizardOrganiserUpdate({
        allOrganisers: organisers,
        selectedOrganiser: organisers.length > 0 ? organisers[0] : getDefaultOrganiserDetails().selectedOrganiser,
      })
    );
    dispatch(onEventWizardTeamNameUpdate(teamName ?? undefined));

    if (teamSize === null || teamSize === 0) {
      dispatch(onEventWizardShareInTeamToggle(false));
    } else {
      dispatch(onEventWizardShareInTeamToggle(getDefaultEventTeamShareDetails()));
    }

    dispatch(
      onEventWizardRegistrationChange({
        ...getDefaultRegistrationDetails(),
        customBtnTexts: customRegisterBtnTexts,
      })
    );
    dispatch(onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate(doesPAYGUserHaveAITemplateLimit));
    dispatch(onEventWizardShouldUpsellRecurring(shouldUpsellRecurring));
    dispatch(onEventWizardUpdateShouldShowRecurringEventDetails(shouldShowRecurringEventDetails));
    dispatch(onEventWizardTicketingGatingPropsUpdate({isCountrySupported: isTicketingCountrySupported, isPaidTicketingAvailable: isPaidTicketingFeatureAvailable, notifyMeEmail}));
  };

  const setDataByPageSourceType = (): void => {
    dispatch(onEventWizardPageSourceTypeUpdate(eventWizardSourceType));

    if (
      eventWizardSourceType === EventWizardSourceType.DEFAULT ||
      eventWizardSourceType === EventWizardSourceType.UPDATE ||
      eventWizardSourceType === EventWizardSourceType.DUPLICATE
    ) {
      setReduxForUpdatingEvent(eventVO);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DESIGN) {
      setReduxForDesignPrefill(eventVOMinifiedWizardPrefill);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DATE_SELECTION) {
      setReduxForStartDatePrefill(eventVOMinifiedWizardPrefill);
    }
  };

  const getBodyContent = (): ReactElement<any> => {
    if (eventWizardContainerQueriesContextValues.isSmallScreen) {
      return (
        <div className={`flex-column-align-start justify-content-center spacing-m-t-4 spacing-p-l-4 spacing-p-r-4 _full-width ${styles.wizardContentMaxWidth}`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
      );
    }

    return (
      <div className={`${styles.contentContainer} flex-row-align-center spacing-m-t-5 spacing-p-l-10 spacing-p-r-10`}>
        <div className={`${styles.content} spacing-m-r-10`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
        <Preview />
      </div>
    );
  };

  return (
    <div className={`${styles.pageContainer} flex-column-align-center`}>
      <EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider value={eventWizardContainerQueriesContextValues}>
        <div ref={containerRef} className=" _full-width _full-height">
          {hasContainerQueryInitialized && isWizardReady ? (
            <div className="flex-column-align-center _full-height _full-width">
              <EventNav />
              {getBodyContent()}
            </div>
          ) : (
            <CircularProgressLoader classes="-for-mystuff-content" />
          )}
        </div>
        <EventFooter />
      </EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider>
    </div>
  );
}
